import { useEffect, useRef } from 'react';
import Swiper, { Scrollbar, SwiperOptions } from 'swiper';
import { SwiperSlide } from 'swiper/react';

import { Field, LinkField, Text, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';

import { ComponentProps } from 'lib/ComponentProps';

import { mapResourceListItem } from 'src/mappers/models/resouceListItemMapper';

import { ExtendedItem } from 'src/types/ExtendedItem';
import { ResourceListItem } from 'src/types/ResourceListItem';

import SliderDragButton from 'public/icons/SliderDragButton.svg';

import ContentCard from '../_ContentCard/_ContentCard';
import { SwiperComponent } from '../_Swiper/SwiperComponent';

import styles from './ContentListing.module.css';

type ContentListingProps = ComponentProps & {
	fields: {
		Title: Field<string>;
		Items: ExtendedItem[];
		WildCardConfig: ExtendedItem;
	};
};

export default withDatasourceCheck()<ContentListingProps>(ContentListing);

function ContentListing(props: ContentListingProps): JSX.Element {
	const curatedList = Array<ResourceListItem>();
	const swiperRef = useRef<Swiper | null>(null);

	if (props.fields.Items?.length) {
		props.fields.Items.map((item) => {
			const resourceListItem = mapResourceListItem(
				item,
				(props.fields.WildCardConfig?.fields.SearchListingWildcardPage as LinkField)?.value?.href
			);
			if (resourceListItem) {
				curatedList.push(resourceListItem);
			}
		});
	}

	const breakpointConfig = () => {
		const breakpoints = [640, 1024, 1440];

		const breakpointObj: { [width: number]: SwiperOptions } = {};

		for (let i = 0; i < breakpoints.length; i++) {
			const slides = i + 2;

			breakpointObj[breakpoints[i]] = {
				slidesPerView: slides >= curatedList.length ? slides : slides + 0.075,
			};

			if (slides === curatedList.length) {
				break;
			}
		}

		return breakpointObj;
	};

	useEffect(() => {
		function destroySwiper() {
			if (!swiperRef.current) {
				return;
			}

			window.innerWidth >= 1440 ? swiperRef.current.disable() : swiperRef.current.enable();
		}

		destroySwiper();

		window.addEventListener('resize', destroySwiper);

		return () => window.removeEventListener('resize', destroySwiper);
	}, []);

	return (
		<section className={`${styles.section} padX maxWidth`}>
			<Text tag="h2" field={props.fields.Title} className={styles.heading} />
			<SwiperComponent
				onSwiper={(swiper) => {
					swiperRef.current = swiper;
				}}
				modules={[Scrollbar]}
				spaceBetween={15}
				slidesPerView={1.075}
				breakpoints={breakpointConfig()}
				scrollbar={{
					el: '.swiper-scrollbar',
					draggable: true,
					dragSize: 48,
					dragClass: 'swiper-scrollbar-drag',
				}}
				className={styles.slider}
			>
				<>
					{curatedList.map((item, index) => {
						return (
							<SwiperSlide key={index}>
								<ContentCard item={item} key={index} />
							</SwiperSlide>
						);
					})}
				</>

				<div className={'swiper-scrollbar'}>
					<SliderDragButton className={'swiper-scrollbar-drag'} />
				</div>
			</SwiperComponent>
		</section>
	);
}
